<template>
	<Header
		title="Export app"
		:is-loading="detailsState.isLoading.value || checkoutLoading" />
	<Container class="checkout">
		<div
			v-if="detailsState.isLoaded.value"
			class="checkout__breakdown-container">
			<SummaryCard
				v-if="app && detailsState.details.value"
				:app-name="detailsState.details.value.name"
				:module-count="app.modules.length - 1"
				:total-time="totalDevelopmentTime"
				:total-value="appMetrics.totalDevelopmentValue.value" />
			<CodeBreakdown
				v-if="app"
				:app-metrics="appMetrics" />
		</div>
		<div
			v-if="detailsState.isLoaded.value"
			class="checkout__sidebar">
			<TokensShort
				v-if="app && tokensShort > 0 && app.modules.length > 6"
				:tokens-needed="app.modules.length - 1"
				:tokens-owned="tokensOwned"
				:tokens-short="tokensShort" />
			<DeliveryOptions
				:details-state="detailsState"
				@update:delivery="onDeliveryUpdated" />
			<TokenPackages
				:tokens-needed="tokensNeeded"
				:tokens-short="tokensShort"
				@update:selected="onPackageSelected" />
			<PurchaseSummary
				v-if="selectedPackage"
				:checkout-loading="checkoutLoading"
				:export-type="delivery.type"
				:selected="selectedPackage"
				:tokens-needed="tokensNeeded"
				:tokens-owned="tokensOwned"
				@checkout="checkout"
				@export="exportApp" />
		</div>
	</Container>
</template>

<script setup lang="ts">
import Header from '@/components/Header.vue'
import StripeApi from '@/helpers/api/StripeApi'
import { computed, onMounted, ref } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import CodeBreakdown from './components/Checkout/CodeBreakdown.vue'
import SummaryCard from './components/Checkout/SummaryCard.vue'
import TokensShort from './components/Checkout/TokensShort.vue'
import TokenPackages from './components/Checkout/TokenPackages.vue'
import PurchaseSummary from './components/Checkout/PurchaseSummary.vue'
import DeliveryOptions from './components/Checkout/DeliveryOptions.vue'
import Container from '@/components/Container.vue'
import { useAppDetailsState } from '@/models/App/States'
import { EnrichedApp } from '@codecannon/schemas/schemas/EnrichedApp'
import ExportsApi from '@/models/Export/Api'
import { useAppMetrics } from './composables/useAppMetrics'
import { useAuthStore } from '@/stores/Auth'
import { useToast } from 'primevue'
import type { Package } from '@/views/App/components/Checkout/TokenPackages.vue'
import { useSchemaValidation } from './composables/useSchemaValidation'

const detailsState = useAppDetailsState()
const route = useRoute()
const router = useRouter()
const auth = useAuthStore()
const stripeApi = new StripeApi()
const toast = useToast()
const authStore = useAuthStore()
const { validateSchema } = useSchemaValidation()

const activeBuild = computed(() => detailsState.details.value?.latest_build)
const app = computed(() =>
	activeBuild.value ? new EnrichedApp({ modules: activeBuild.value!.app_schema }) : null,
)
const appMetrics = useAppMetrics(app)
const selectedPackage = ref<Package | null>(null)
const delivery = ref<{ type: 'zip' | 'git'; repositoryId?: string | null }>({ type: 'git' })
const checkoutLoading = ref(false)

const tokensOwned = computed(() => {
	return auth.user?.tokens_remaining ?? 0
})
const tokensNeeded = computed(() => {
	return (app.value?.modules.length ?? 1) - 1
})
const tokensShort = computed(() => {
	if (tokensOwned.value >= tokensNeeded.value) return 0

	return tokensNeeded.value - tokensOwned.value
})

const totalDevelopmentTime = computed(() => ({
	hours: Math.floor(appMetrics.totalDevelopmentTime.value / 60),
	minutes: appMetrics.totalDevelopmentTime.value % 60,
}))

onMounted(async () => {
	await getDetails()
})

function onPackageSelected(pkg: Package) {
	selectedPackage.value = pkg
}

function onDeliveryUpdated(payload: { type: 'zip' | 'git'; repositoryId?: string | null }) {
	delivery.value = payload
}

async function getDetails() {
	await detailsState.getDetails(route.params.id as string, {
		with: ['latestBuild', 'repositoryConfigs'],
	})
}

async function checkout() {
	if (!selectedPackage.value?.price_id || !selectedPackage.value?.quantity) return
	if (delivery.value.type === 'git') {
		if (!delivery.value.repositoryId) {
			toast.add({
				severity: 'error',
				summary: 'No repository selected',
				/**
				 * Detail content of the message.
				 */
				detail: 'To export to git you must first add and select a repository',
				life: 5000,
			})
			return
		}
	}
	checkoutLoading.value = true
	try {
		await stripeApi.checkout({
			price_id: selectedPackage.value.price_id,
			quantity: selectedPackage.value.quantity,
			build_id: activeBuild.value!.id,
			delivery_type: delivery.value.type,
			repository_config_id: delivery.value.repositoryId ?? undefined,
		})
	} finally {
		checkoutLoading.value = false
	}
}

async function exportApp() {
	if (!activeBuild.value?.id || !validateSchema(app.value, route.params.id as string)) return

	checkoutLoading.value = true
	let res: Awaited<ReturnType<ExportsApi['export']>>
	try {
		if (delivery.value.type === 'git') {
			if (!delivery.value.repositoryId) {
				toast.add({
					severity: 'error',
					summary: 'No repository selected',
					/**
					 * Detail content of the message.
					 */
					detail: 'To export to git you must first add and select a repository',
					life: 5000,
				})
				return
			}

			res = await new ExportsApi().export({
				build_id: activeBuild.value.id,
				delivery: {
					type: delivery.value.type,
					repository_config_id: delivery.value.repositoryId,
				},
			})
		} else {
			res = await new ExportsApi().export({
				build_id: activeBuild.value.id,
				delivery: {
					type: 'zip',
				},
			})
		}
		authStore.getUser()
		router.push({ name: 'apps-details', query: { tab: 'exports', exportId: res.data.export.id } })
	} finally {
		checkoutLoading.value = false
	}
}
</script>

<style scoped lang="scss">
.checkout {
	display: flex;
	justify-content: center;
	gap: 16px;
	background-color: var(--p-surface-0);
	flex: 1;
	min-height: 2px;

	@media all {
		background-color: var(--p-surface-950);
	}

	@media only screen and (max-width: 1200px) {
		flex-direction: column;
		justify-content: start;
		align-items: center;
	}

	.checkout__breakdown-container {
		display: flex;
		flex-direction: column;
		gap: 16px;
		width: 100%;
		max-width: 800px;

		@media only screen and (max-width: 1200px) {
			width: 100%;
			max-width: 100%;
		}
	}

	.checkout__sidebar {
		width: 400px;
		display: flex;
		flex-direction: column;
		gap: 12px;

		@media only screen and (max-width: 1200px) {
			width: 100%;
			max-width: 100%;
		}
	}
}
</style>
